<template>
    <section-wrapper
        :class="Section.INVESTMENT_PROPOSAL.cssClassName"
        :this-section="Section.INVESTMENT_PROPOSAL"
        :next-section="Section.APPOINTMENT"
        :section-end-button-in-dom="false">
        <h2>{{ $t('investmentProposal.title') }}</h2>
        <div class="row">
            <div class="col">
                <p class="investment-proposal__lead">
                    {{ $t('investmentProposal.text') }}
                </p>
            </div>
        </div>

        <div v-if="!isScreenSourceWebAppMobile" class="investment-proposal__radio-section-wrapper">
            <div class="investment-proposal__radio-text">
                {{ $t('investmentProposal.userIsCustomer.text') }}
            </div>
            <div class="investment-proposal__radio-buttons">
                <Icon
                    class="radio-icon"
                    @onClick="handleUserIsCustomer(true)"
                    :type="userIsCustomer ? 'radio_checked_dark' : 'radio'"
                    :width="25"
                    :height="25" />
                {{ $t('investmentProposal.userIsCustomer.yes') }}
                <Icon
                    class="radio-icon"
                    @onClick="handleUserIsCustomer(false)"
                    :type="userIsCustomer === false ? 'radio_checked_dark' : 'radio'"
                    :width="25"
                    :height="25" />
                {{ $t('investmentProposal.userIsCustomer.no') }}
            </div>
        </div>

        <div
            class="investment-proposal__boxes-wrapper">
            <div class="row investment-proposal__email-and-qr-code">
                <div class="col investment-proposal__box-wrapper">
                    <div class="investment-proposal__box">
                        <h3 class="h3--black">
                            {{ investmentProposalBoxTitle }}
                        </h3>
                        <p class="investment-proposal__text">
                            {{ investmentProposalBoxText }}
                        </p>
                        <template v-if="displaySendEmail">
                            <input
                                v-model="emailInput"
                                @focus="resetEmailSendingAbility"
                                @blur="validateEmailAndCheckForSendingAbility"
                                :class="['investment-proposal__input', 'form-control', emailValidationClasses]"
                                @paste.prevent="onPasteEmail">
                            <p v-if="emailValidationEnabled"
                               :class="['investment-proposal__validation-message', emailTextValidationClasses]">
                                {{ emailValidationMessage }}
                            </p>
                            <button
                                class="btn btn-primary"
                                :disabled="!enableSendEmailButton"
                                @click="onEmailRequest">
                                <Icon
                                    type="button-arrow"
                                    :height="16"
                                    :width="9" />
                                {{ investmentProposalBoxButtonLabel }}
                            </button>
                        </template>
                        <template v-else>
                            <button
                                class="btn btn-primary"
                                @click="onApplyNow">
                                <Icon
                                    type="button-arrow"
                                    :height="16"
                                    :width="9" />
                                {{ investmentProposalBoxButtonLabel }}
                            </button>
                        </template>
                    </div>
                </div>
            </div>

            <qr-code-box
                class="investment-proposal_qr-code-box"
                v-if="qrCode"
                :user-is-customer="this.userIsCustomer"
                :qr-code="qrCode"
                @qrCodeBoxDestroyed="resetQrCode" />

            <div class="row">
                <div class="col">
                    <p class="investment-proposal__appointment-text">
                        {{ $t('investmentProposal.closeWith.appointment.text') }}
                    </p>
                    <icon-link
                        v-if="viewportNoMatch"
                        :href="settings.onlineAppointmentSchedulerUrl"
                        :label="$t('investmentProposal.closeWith.appointment.linkLabel')" />
                    <icon-link
                        v-if="viewportIsLarge"
                        :prevent-default-click-event="true"
                        @onClick="onAppointmentLinkClick"
                        :label="$t('investmentProposal.closeWith.appointment.linkLabel')" />
                </div>
            </div>
            <div
                class="box-cover"
                v-if="userIsCustomer === null" />
        </div>
    </section-wrapper>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import QrCodeBox from '@/components/common/QrCodeBox';
import SectionWrapper from '@/components/common/SectionWrapper';
import IconLink from '@/components/common/IconLink';
import { Section } from '@/enums';
import { scrollToNewElement } from '@/services/scrollToService';

export default {
    name: 'InvestmentProposal',
    components: {
        QrCodeBox,
        SectionWrapper,
        IconLink,
    },
    data () {
        return {
            Section,
            emailInput: '',
            emailSentTo: '',
            enableSendEmailButton: false,
            emailHasBeenSent: false,
            emailValidationEnabled: false,
            qrCode: null,
            showApplyButton: true,
        };
    },
    watch: {
        userIsCustomer () {
            // if condition is true, the QrCodeBox is already visible and thus its content needs to be updated
            if (this.qrCode !== null) {
                this.persistConfigurationDataAndFetchQrCode();
            }

            this.resetEmailValidationAndSendingAbility();
        },
        openedSection () {
            this.resetEmailValidationAndSendingAbility();
            this.showApplyButton = true;
        },
        emailInput (value) {
            this.setEmailAddress(value);
            if (this.isValidAndNonEmptyEmail) {
                this.validateEmailAndCheckForSendingAbility();
            }
        },
    },
    computed: {
        ...mapGetters(['viewportNoMatch', 'viewportIsLarge', 'settings', 'isScreenSourceWebAppMobile']),
        ...mapState(['emailAddress', 'userIsCustomer', 'openSections']),
        displaySendEmail () {
            const settingsKey = this.userIsCustomer
                ? 'emailRequiredForExistingCustomer'
                : 'emailRequiredForNewCustomer';

            return this.settings[settingsKey];
        },
        investmentProposalTranslateKey () {
            return this.displaySendEmail
                ? 'userIsNotCustomer'
                : 'userIsCustomer';
        },
        investmentProposalBoxTitle () {
            return this.$t(`investmentProposal.closeWith.${this.investmentProposalTranslateKey}.email.title`);
        },
        investmentProposalBoxText () {
            return this.$t(`investmentProposal.closeWith.${this.investmentProposalTranslateKey}.email.text`);
        },
        investmentProposalBoxButtonLabel () {
            return this.$t(`investmentProposal.closeWith.${this.investmentProposalTranslateKey}.email.buttonLabel`);
        },
        isValidEmail () {
            return this.$options.filters.validEmail(this.emailAddress);
        },
        isValidAndNonEmptyEmail () {
            return (this.emailAddress && this.isValidEmail);
        },
        openedSection () {
            return this.openSections[this.openSections.length - 1];
        },
        emailValidationClasses () {
            return {
                'is-valid': this.emailHasBeenSent,
                'is-invalid': this.emailValidationEnabled && !this.isValidEmail,
            };
        },
        emailValidationMessage () {
            if (this.emailValidationEnabled) {
                if (this.emailHasBeenSent) {
                    return this.$t('investmentProposal.closeWith.email.success');
                } else if (!this.isValidEmail) {
                    return this.$t('investmentProposal.closeWith.email.error');
                }
            }

            return '';
        },
        emailTextValidationClasses () {
            return {
                'is-valid-feedback': this.emailHasBeenSent,
                'is-invalid-feedback': this.emailValidationEnabled && !this.isValidEmail,
            };
        },
    },
    methods: {
        ...mapActions(['setEmailAddress', 'setUserIsCustomer', 'saveConfigurationDataAndSendEmail', 'saveConfigurationData', 'addOpenSection', 'removeOpenSection']),
        onAppointmentLinkClick () {
            const appointmentSection = Section.APPOINTMENT;
            if (this.openSections.includes(appointmentSection)) {
                this.removeOpenSection(appointmentSection);
            } else {
                this.addOpenSection(appointmentSection);
                this.$nextTick(() => {
                    scrollToNewElement('.appointment');
                });
            }
        },
        onApplyNow () {
            this.persistConfigurationDataAndFetchQrCode();
        },
        onEmailRequest () {
            this.enableSendEmailButton = false;
            this.saveConfigurationDataAndSendEmail()
                .then(() => {
                    this.emailHasBeenSent = true;
                    this.emailSentTo = this.emailAddress;
                    this.persistConfigurationDataAndFetchQrCode();
                });
        },
        persistConfigurationDataAndFetchQrCode () {
            this.saveConfigurationData()
                .then(({ qrCode }) => {
                    this.qrCode = qrCode;
                    this.$nextTick(() => {
                        scrollToNewElement('.investment-proposal_qr-code-box');
                        this.showApplyButton = false;
                    });
                });
        },
        handleUserIsCustomer (flag) {
            this.resetQrCode();
            this.setUserIsCustomer(flag);
        },
        resetQrCode () {
            this.qrCode = null;
        },
        validateEmailAndCheckForSendingAbility () {
            this.emailValidationEnabled = true;

            if (this.isValidAndNonEmptyEmail && this.emailAddress !== this.emailSentTo) {
                this.emailSentTo = '';
                this.enableSendEmailButton = true;
            } else if (!this.isValidAndNonEmptyEmail) {
                this.enableSendEmailButton = false;
            }
        },
        resetEmailSendingAbility () {
            this.emailHasBeenSent = false;
        },
        resetEmailValidationAndSendingAbility () {
            this.emailHasBeenSent = false;
            this.emailValidationEnabled = false;
            this.enableSendEmailButton = false;

            if (this.isValidAndNonEmptyEmail) {
                this.emailValidationEnabled = true;
                this.enableSendEmailButton = true;
            }
        },
        onPasteEmail (event) {
            let text = '';
            if (window.clipboardData) { // IE11
                text = window.clipboardData.getData('Text');
            } else if (event.clipboardData) {
                text = event.clipboardData.getData('text/plain');
            }
            this.emailInput = text.trim();
        },
    },
};
</script>

<style scoped lang="scss">
  .h3--black {
    color: $mb-grey5;

    @include media-breakpoint-down(xs) {
      font-size: 16px;
    }
  }

  .investment-proposal__lead {
    margin-bottom: 30px;

    @include media-breakpoint-down(xs) {
      margin-bottom: 15px;
    }
  }

  .investment-proposal__radio-section-wrapper {
    @include font-migrosbank();
    color: $mb-grey5;
    font-size: 18px;
    line-height: 23px;
    display: flex;
    margin-bottom: 30px;

    @include media-breakpoint-down(xs) {
      display: block;
      font-size: 15px;
      margin-bottom: 15px;
    }
  }

  .investment-proposal__radio-text {
    @include media-breakpoint-down(xs) {
      margin-bottom: 15px;
    }
  }

  .investment-proposal__radio-buttons {
    display: flex;
    align-items: center;

    .radio-icon {
      margin-left: 40px;
      margin-right: 10px;
      cursor: pointer;

      @include media-breakpoint-down(xs) {
        margin-left: 10px;

        & ~ .radio-icon {
          margin-left: 30px;
        }
      }
    }

  }

  .investment-proposal__boxes-wrapper {
    position: relative;
  }

  .box-cover {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(255, 255, 255, 0.5);
  }

  .investment-proposal__input {
    margin-bottom: 5px;
    max-width: 50%;

    @include media-breakpoint-down(xs) {
      margin-bottom: 10px;
      max-width: 100%;
    }
  }

  .investment-proposal__text {
    margin-bottom: 20px;

    @include media-breakpoint-down(xs) {
      margin-bottom: 10px;
    }
  }

  .investment-proposal_qr-code-box {
    margin-top: 15px;

    @include media-breakpoint-down(xs) {
      margin-top: 10px;
    }
  }

  .investment-proposal__appointment-text {
    margin-top: 20px;
    margin-bottom: 12px;

    @include media-breakpoint-down(xs) {
      margin-top: 15px;
      margin-bottom: 0;
    }
  }

  .investment-proposal__email-and-qr-code {

    @include media-breakpoint-down(xs) {
      margin-bottom: 0;

      .investment-proposal__box-wrapper {
        &~ .investment-proposal__box-wrapper {
          margin-top: 10px;
        }
      }
    }
  }

  .investment-proposal__box {
    position: relative;
    background-color: $mb-grey-12;
    padding: 20px 15px;
    height: 100%;

    &.bg-grey {
      background-color: $mb-grey-14;
    }

    @include media-breakpoint-down(xs) {
      padding: 15px 10px;
    }
  }

  .investment-proposal__request-button {
    @include media-breakpoint-up(lg) {
      position: absolute;
      bottom: 20px;
    }
  }

  .is-valid-feedback {
    color: $interaction-green;
  }

  .is-invalid-feedback {
    color: $interaction-red;
  }
</style>
